import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { requestUploadURL, uploadToS3, pollDocumentStatus } from '../utils/api';
import { calculateSHA256 } from '../utils/fileUtils';
import { useLibrary } from '../context/LibraryContext';
import { useApi } from '../hooks/useApi';
import LibrarySelectModal from './LibrarySelectModal';
import SelfScanButton from './SelfScanButton';
import iconUpload from '../assets/icon-upload.png';
import loadingSign from '../assets/transparent_cc_logo.png';
import './TwoZoneUpload.css';

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const ALLOWED_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

function CompareIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <rect x="1" y="3" width="9" height="12" rx="1.5" stroke="white" strokeWidth="1.5"/>
      <rect x="8" y="3" width="9" height="12" rx="1.5" stroke="white" strokeWidth="1.5" fill="#1B6EB1"/>
    </svg>
  );
}

function FileCard({ file, onRemove }) {
  const isComplete = file.status === 'completed';
  const isError = file.status === 'error';

  return (
    <div className="tz-file-card">
      <div className="tz-file-icon-wrap" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M5 2h7l4 4v12a1 1 0 01-1 1H5a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="#4B7BE8" strokeWidth="1.5"/>
          <path d="M12 2v4h4" stroke="#4B7BE8" strokeWidth="1.5"/>
          <path d="M7 9h6M7 12h4" stroke="#4B7BE8" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </div>
      <div className="tz-file-info">
        <div className="tz-file-name">{file.name}</div>
        {!isError && (
          <div className="tz-progress-track">
            <div
              className={`tz-progress-fill${isComplete ? ' complete' : ''}`}
              style={{ width: `${file.progress}%` }}
            />
          </div>
        )}
        <div className="tz-progress-footer">
          <span className="tz-file-status">
            {isComplete ? 'Upload Successful!' : isError ? `Error: ${file.error}` : 'Uploading...'}
          </span>
          {!isError && <span className="tz-file-pct">{file.progress}%</span>}
        </div>
      </div>
      <div className="tz-file-actions">
        {isComplete && <span className="tz-checkmark">✓</span>}
        <button
          className="tz-remove-btn"
          onClick={() => onRemove(file.id)}
          aria-label={`Remove ${file.name}`}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 4h10M6 4V2.5h4V4M5.5 4l.5 9M10.5 4l-.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

function DropZone({ label, sublabel, files, onFilesSelected, onLibraryClick, isDragging, onDragOver, onDragLeave, onDrop, inputRef, multiple, emptyHint }) {
  const hasFiles = files.length > 0;

  return (
    <div className="upload-zone-section">
      <h2>{label}</h2>
      <p>{sublabel}</p>
      {!hasFiles && emptyHint && <p className="tz-empty-hint">{emptyHint}</p>}
      {!hasFiles && (
        <label
          className={`tz-dropzone${isDragging ? ' dragging' : ''}`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          aria-label="Click to upload file"
        >
          <input
            ref={inputRef}
            type="file"
            multiple={multiple}
            accept=".pdf,.doc,.docx"
            onChange={(e) => onFilesSelected(null, 'add', e.target.files)}
            style={{ position: 'absolute', width: 0, height: 0, opacity: 0, overflow: 'hidden' }}
          />
          <img src={iconUpload} alt="" className="tz-dropzone-icon" />
          <div className="tz-dropzone-text">Drag and drop files here or:</div>
          <div className="tz-btn-row">
            <span className="tz-btn-primary">Upload from computer</span>
            <span
              className="tz-btn-secondary"
              role="button"
              tabIndex={0}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onLibraryClick(); }}
              onKeyDown={(e) => e.key === 'Enter' && onLibraryClick()}
            >
              Select from library
            </span>
          </div>
          <div className="tz-dropzone-hint">Supported Format: PDF, Word, (20mb each)</div>
        </label>
      )}
      {hasFiles && (
        <div>
          <div className="tz-file-list">
            {files.map((f) => (
              <FileCard key={f.id} file={f} onRemove={(id) => onFilesSelected(id, 'remove')} />
            ))}
          </div>
          <div className="tz-btn-row" style={{ marginTop: 12 }}>
            <label className="tz-btn-primary" style={{ cursor: 'pointer', userSelect: 'none' }}>
              <input
                ref={inputRef}
                type="file"
                multiple={multiple}
                accept=".pdf,.doc,.docx"
                onChange={(e) => onFilesSelected(null, 'add', e.target.files)}
                style={{ position: 'absolute', width: 0, height: 0, opacity: 0, overflow: 'hidden' }}
              />
              Upload from computer
            </label>
            <button className="tz-btn-secondary" onClick={onLibraryClick}>
              Select from library
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TwoZoneUpload({ onCompare, isComparing }) {
  const [primaryFiles, setPrimaryFiles] = useState([]);
  const [refFiles, setRefFiles] = useState([]);
  const [primaryDragging, setPrimaryDragging] = useState(false);
  const [refDragging, setRefDragging] = useState(false);
  const [libraryModal, setLibraryModal] = useState({ open: false, zone: null });

  const primaryInputRef = useRef(null);
  const refInputRef = useRef(null);

  const { documents, addDocument, updateDocumentStatus, removeLocalDocument } = useLibrary();
  const { fetchAPI } = useApi();

  const updateFile = (setFiles, id, updates) =>
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...updates } : f)));

  const processFile = async (fileObj, setFiles) => {
    try {
      updateFile(setFiles, fileObj.id, { status: 'hashing', progress: 10 });
      const fileHash = await calculateSHA256(fileObj.file);

      const duplicate = documents?.find((d) => d.fileHash === fileHash || d.md5Hash === fileHash);
      if (duplicate) {
        updateFile(setFiles, fileObj.id, {
          status: 'completed',
          progress: 100,
          documentId: duplicate.id,
          policyId: duplicate.policyId || duplicate.policy_id,
        });
        return;
      }

      let response;
      try {
        response = await requestUploadURL(fetchAPI, fileObj.file, fileHash);
      } catch (err) {
        if (err.status === 409) {
          updateFile(setFiles, fileObj.id, {
            status: 'completed',
            progress: 100,
            documentId: err.data.document_id,
            policyId: err.data.policy_id || null,
          });
          return;
        }
        throw err;
      }

      if (response.duplicate) {
        updateFile(setFiles, fileObj.id, {
          status: 'completed',
          progress: 100,
          documentId: response.document_id,
          policyId: response.policy_id || null,
        });
        return;
      }

      const documentId = response.document_id;
      addDocument({ id: documentId, name: fileObj.file.name, status: 'uploading' });
      updateFile(setFiles, fileObj.id, { status: 'uploading', progress: 30, documentId });

      await uploadToS3(response.upload_url, fileObj.file, response.upload_fields || {});
      updateDocumentStatus(documentId, { status: 'processing' });
      updateFile(setFiles, fileObj.id, { status: 'processing', progress: 60 });

      const result = await pollDocumentStatus(fetchAPI, documentId);
      updateDocumentStatus(documentId, { status: 'processed', completedAt: new Date().toISOString() });
      updateFile(setFiles, fileObj.id, {
        status: 'completed',
        progress: 100,
        policyId: result.policy_id || result.policyId,
      });
    } catch (err) {
      const failedDocId = fileObj.documentId;
      if (failedDocId) removeLocalDocument(failedDocId);
      updateFile(setFiles, fileObj.id, { status: 'error', error: err.message });
    }
  };

  const addFiles = (rawFiles, setFiles, maxFiles = Infinity) => {
    const arr = Array.from(rawFiles).slice(0, maxFiles).filter((file) => {
      if (!ALLOWED_TYPES.includes(file.type)) return false;
      if (file.size > MAX_FILE_SIZE_BYTES) return false;
      return true;
    });
    const newFiles = arr.map((file) => ({
      file,
      id: crypto.randomUUID(),
      name: file.name,
      status: 'pending',
      progress: 0,
      documentId: null,
      policyId: null,
      error: null,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
    newFiles.forEach((f) => processFile(f, setFiles));
  };

  const handlePrimaryAction = (id, action, rawFiles) => {
    if (action === 'remove') {
      const file = primaryFiles.find((f) => f.id === id);
      if (file?.documentId) removeLocalDocument(file.documentId);
      setPrimaryFiles((prev) => prev.filter((f) => f.id !== id));
    } else if (action === 'add' && rawFiles?.length) {
      setPrimaryFiles([]);
      addFiles(rawFiles, setPrimaryFiles, 1);
    }
  };

  const handleRefAction = (id, action, rawFiles) => {
    if (action === 'remove') {
      const file = refFiles.find((f) => f.id === id);
      if (file?.documentId) removeLocalDocument(file.documentId);
      setRefFiles((prev) => prev.filter((f) => f.id !== id));
    } else if (action === 'add' && rawFiles?.length) {
      addFiles(rawFiles, setRefFiles);
    }
  };

  const handleLibraryConfirm = (selected) => {
    const zone = libraryModal.zone;
    setLibraryModal({ open: false, zone: null });
    if (zone === 'primary') {
      const doc = selected[0];
      if (!doc) return;
      setPrimaryFiles([{
        id: crypto.randomUUID(),
        name: doc.name,
        status: 'completed',
        progress: 100,
        documentId: doc.id,
        policyId: doc.policyId || doc.policy_id,
        error: null,
      }]);
    } else {
      setRefFiles(selected.map((doc) => ({
        id: crypto.randomUUID(),
        name: doc.name,
        status: 'completed',
        progress: 100,
        documentId: doc.id,
        policyId: doc.policyId || doc.policy_id,
        error: null,
      })));
    }
  };

  const primaryReady = primaryFiles.length === 1 && primaryFiles[0].status === 'completed';
  const refReady = refFiles.length > 0 && refFiles.every((f) => f.status === 'completed');
  const showCompare = primaryReady && refReady;
  // Inverse of refReady's emptiness check, not refReady itself — avoids flicker while refs are mid-upload.
  const showSelfScan = primaryReady && refFiles.length === 0;

  const handleCompare = () => {
    if (!showCompare || isComparing) return;
    onCompare({
      primaryFile: primaryFiles[0],
      referenceFiles: refFiles,
    });
  };

  const handleSelfScan = () => {
    if (!showSelfScan || isComparing) return;
    onCompare({
      primaryFile: primaryFiles[0],
      referenceFiles: [],
      isSelf: true,
    });
  };

  return (
    <div className="two-zone-upload">
      <div className="tz-zones-row">
        <DropZone
          label="Primary document"
          sublabel="Upload the document you want to check for policy compliance (1 file only)"
          files={primaryFiles}
          onFilesSelected={handlePrimaryAction}
          onLibraryClick={() => setLibraryModal({ open: true, zone: 'primary' })}
          isDragging={primaryDragging}
          onDragOver={(e) => { e.preventDefault(); setPrimaryDragging(true); }}
          onDragLeave={() => setPrimaryDragging(false)}
          onDrop={(e) => { e.preventDefault(); setPrimaryDragging(false); handlePrimaryAction(null, 'add', e.dataTransfer.files); }}
          inputRef={primaryInputRef}
          multiple={false}
        />

        <DropZone
          label="Reference files"
          sublabel="Upload one or more policy documents to compare against (multiple files accepted)"
          files={refFiles}
          onFilesSelected={handleRefAction}
          onLibraryClick={() => setLibraryModal({ open: true, zone: 'ref' })}
          isDragging={refDragging}
          onDragOver={(e) => { e.preventDefault(); setRefDragging(true); }}
          onDragLeave={() => setRefDragging(false)}
          onDrop={(e) => { e.preventDefault(); setRefDragging(false); handleRefAction(null, 'add', e.dataTransfer.files); }}
          inputRef={refInputRef}
          multiple={true}
          emptyHint="Leave empty to check the primary document against itself."
        />
      </div>

      {showCompare && (
        <div className="tz-compare-row">
          <button
            className="tz-compare-btn"
            onClick={handleCompare}
            disabled={isComparing}
            aria-label="Compare"
          >
            <CompareIcon />
            Compare
          </button>
        </div>
      )}

      {showSelfScan && (
        <SelfScanButton
          filename={primaryFiles[0].name}
          onClick={handleSelfScan}
          disabled={isComparing}
        />
      )}

      {isComparing && createPortal(
        <div className="tz-loading-overlay">
          <div className="tz-loading-box">
            <img src={loadingSign} alt="" className="tz-loading-logo" />
            <span className="tz-loading-text">Loading...</span>
          </div>
        </div>,
        document.body
      )}

      <LibrarySelectModal
        isOpen={libraryModal.open}
        mode={libraryModal.zone === 'primary' ? 'single' : 'multi'}
        onConfirm={handleLibraryConfirm}
        onClose={() => setLibraryModal({ open: false, zone: null })}
      />
    </div>
  );
}
